/* CUSTOM_SWATCHHOVER_BY_CLAUDE (Ryan 2026-10-21) — colour name follows the mouse, site-wide.
   (Also drives the selected ring's draw-on; see the end of this file.)

   Ryan: hovering a colour circle should change the "Frame Color: <name>" read-out to that colour;
   a click locks it; leaving the circles returns it to the locked one. And the name should not
   snap — it animates in, quickly and elegantly, so the change is noticed.

   Works on EVERY circle picker without knowing which kind it is: the variant picker's read-out
   (`.js-color-text`, snippets/variant-picker.liquid) and the line-item-property swatches'
   (`.js-swatch-prop-value`, main-product.liquid `swatches` custom option) both carry
   `.option-selector__label-value`. The hovered colour's name is the value of the radio the
   circle's <label for> points at, so nothing here holds a list of colours.

   Display only. It never checks a radio and never fires a change, so it cannot select a variant,
   trigger a section refetch or alter what posts to the cart. The scripts that own the read-out
   still write it on a real selection; this one restores the checked value when the mouse leaves.

   Mouse only (pointerType), so a touch tap is exactly what it was. Delegated from the document, so
   it survives the variant picker's wholesale section swaps and quick-buy clones. The animation is
   transform / opacity / filter through the Web Animations API — compositor-friendly, and each new
   hover cancels the last instead of queueing, so sweeping along a row of 30 stays smooth.
   prefers-reduced-motion gets the text change with no motion. */
(function () {
  'use strict';
  if (window.__bsfsSwatchHoverBound) return;
  window.__bsfsSwatchHoverBound = true;

  var BRAND_BLUE = '#5ba8f7';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  var style = document.createElement('style');
  style.textContent = '.option-selector__label-value.bsfs-swatch-name{display:inline-block;will-change:transform,opacity}';
  document.head.appendChild(style);

  function readoutFor(el) {
    var selector = el && el.closest('.option-selector');
    return selector ? selector.querySelector('.option-selector__label-value') : null;
  }

  function hoveredValue(label) {
    var input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
    if (input && input.value) return input.value;
    var hidden = label.querySelector('.js-value');
    return hidden ? hidden.textContent.trim() : '';
  }

  function checkedValue(readout) {
    var input = readout.closest('.option-selector').querySelector('input[type="radio"]:checked');
    return input ? input.value : null;
  }

  function show(readout, text) {
    if (!text || readout.textContent === text) return;
    readout.textContent = text;
    if (!readout.animate || (reduceMotion && reduceMotion.matches)) return;
    readout.classList.add('bsfs-swatch-name');
    if (readout.__bsfsAnim) readout.__bsfsAnim.cancel();
    var settled = getComputedStyle(readout).color;
    readout.__bsfsAnim = readout.animate([
      { opacity: 0, transform: 'translateY(0.45em)', filter: 'blur(3px)', color: BRAND_BLUE },
      { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)', color: BRAND_BLUE, offset: 0.55 },
      { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)', color: settled }
    ], { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
  }

  document.addEventListener('pointerover', function (evt) {
    if (evt.pointerType !== 'mouse') return;
    var label = evt.target.closest && evt.target.closest('.opt-label--swatch');
    if (!label) return;
    var readout = readoutFor(label);
    if (readout) show(readout, hoveredValue(label));
  });

  // Leaving the row of circles (not merely moving between two of them) restores the locked colour.
  document.addEventListener('pointerout', function (evt) {
    if (evt.pointerType !== 'mouse') return;
    var row = evt.target.closest && evt.target.closest('.option-selector__btns');
    if (!row || (evt.relatedTarget && row.contains(evt.relatedTarget))) return;
    var readout = readoutFor(row);
    if (readout) show(readout, checkedValue(readout));
  });

  /* The selected ring's draw-on (assets/product.css, CUSTOM_SWATCHSELECTED_BY_CLAUDE). Started here
     on a click rather than by CSS on :checked, because a variant-picker click re-renders the option
     column a moment later and a :checked animation replayed on the new circle (Ryan saw it draw
     twice). Keyed by the radio's id, which the re-render keeps, so the replacement circle RESUMES.

     ⚠️ Progress is measured in frames actually painted (requestAnimationFrame), never by the clock.
     On heavier pages (ECORE 2x2 tiles and sample, the 3x cages, the NP2 nameplate) the re-render
     freezes the main thread, the sweep — an animated custom property, so main-thread — sits still,
     and a clock-based resume found the draw "already finished" and showed the full ring with no
     draw at all. Resuming from the last painted frame continues from what the customer saw.
     A circle that renders without a click (page load, a quick-buy clone) shows the finished ring. */
  var RING_MS = 420;
  var ring = {};   // radio id -> { seen (ms of draw actually painted), done, label }

  function play(input, fromMs) {
    var label = input.nextElementSibling;
    if (!label || !label.matches('label[data-swatch]') || !input.id) return;
    if (reduceMotion && reduceMotion.matches) return;
    var st = ring[input.id] = { seen: fromMs, done: false, label: label };
    label.classList.remove('bsfs-ring-draw');
    label.style.setProperty('--bsfs-ring-delay', (-fromMs) + 'ms');
    void label.offsetWidth;
    label.classList.add('bsfs-ring-draw');
    // Count painted frames only. A gap over 100 ms is a frozen main thread: the CSS clock ran on
    // unseen, so rewind the draw to what was actually painted before the next frame shows.
    var last = null;
    (function tick(now) {
      if (ring[input.id] !== st || st.done || !label.isConnected) return;
      if (now !== undefined) {
        if (last !== null) {
          if (now - last > 100) { play(input, st.seen); return; }
          st.seen = Math.min(RING_MS, st.seen + (now - last));
        }
        last = now;
      }
      requestAnimationFrame(tick);
    })();
  }

  document.addEventListener('change', function (evt) {
    var input = evt.target;
    if (!input || input.type !== 'radio' || !input.closest('.option-selector')) return;
    if (!input.nextElementSibling || !input.nextElementSibling.matches('label[data-swatch]')) return;
    play(input, 0);
  });

  document.addEventListener('animationend', function (evt) {
    if (evt.animationName !== 'bsfs-ring-draw') return;
    var label = evt.target;
    label.classList.remove('bsfs-ring-draw');
    var st = label.htmlFor && ring[label.htmlFor];
    // An end reached on the CSS clock while frames were frozen is not an end the customer saw.
    if (st && st.label === label && st.seen >= RING_MS - 60) st.done = true;
  });

  new MutationObserver(function (records) {
    records.forEach(function (rec) {
      rec.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1 || !node.querySelectorAll) return;
        node.querySelectorAll('.option-selector input[type="radio"]:checked').forEach(function (input) {
          var st = ring[input.id];
          if (st && !st.done && st.seen < RING_MS && st.label !== input.nextElementSibling) play(input, st.seen);
        });
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
/* END CUSTOM_SWATCHHOVER_BY_CLAUDE */
